import { supabase } from '@/integrations/supabase/client';
import { debugLog, debugError, debugWarn } from '@/lib/debugUtils';
import type { 
  Workspace, 
  CreateWorkspaceParams, 
  CreateWorkspaceResult, 
  WorkspaceError, 
  WorkspaceErrorType,
  WorkspaceServiceConfig,
  WorkspaceValidationRules
} from '../types/workspace';

/**
 * Default workspace validation rules
 */
const DEFAULT_WORKSPACE_VALIDATION: WorkspaceValidationRules = {
  name: {
    required: true,
    minLength: 1,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_]+$/
  },
  description: {
    maxLength: 500
  }
};

/**
 * Default workspace service configuration
 */
const DEFAULT_WORKSPACE_CONFIG: WorkspaceServiceConfig = {
  enableDuplicateNames: true, // Allow duplicate names (recommended approach)
  autoGenerateDescription: true,
  validationRules: DEFAULT_WORKSPACE_VALIDATION
};

/**
 * Enhanced workspace service with comprehensive error handling and validation
 * Following Software Architect best practices for clean, maintainable code
 * Uses application-level security with explicit user filtering (RLS disabled)
 */
export class WorkspaceService {
  private config: WorkspaceServiceConfig;

  constructor(config: WorkspaceServiceConfig = DEFAULT_WORKSPACE_CONFIG) {
    this.config = config;
  }

  /**
   * Create enhanced error object
   */
  private createError(
    type: WorkspaceErrorType,
    message: string,
    details?: any,
    field?: string
  ): WorkspaceError {
    return {
      type,
      message,
      details,
      field
    };
  }

  /**
   * Validate workspace creation parameters
   */
  private validateCreateParams(params: CreateWorkspaceParams): void {
    const { name, description } = params;
    const { validationRules } = this.config;

    // Name validation
    if (!name || typeof name !== 'string') {
      throw this.createError(
        'validation_error',
        'Workspace name is required',
        { field: 'name' },
        'name'
      );
    }

    const trimmedName = name.trim();
    
    if (trimmedName.length < validationRules.name.minLength) {
      throw this.createError(
        'validation_error',
        `Workspace name must be at least ${validationRules.name.minLength} character(s)`,
        { field: 'name', minLength: validationRules.name.minLength },
        'name'
      );
    }

    if (trimmedName.length > validationRules.name.maxLength) {
      throw this.createError(
        'validation_error',
        `Workspace name must not exceed ${validationRules.name.maxLength} characters`,
        { field: 'name', maxLength: validationRules.name.maxLength },
        'name'
      );
    }

    // Pattern validation if specified
    if (validationRules.name.pattern && !validationRules.name.pattern.test(trimmedName)) {
      throw this.createError(
        'validation_error',
        'Workspace name contains invalid characters. Only letters, numbers, spaces, hyphens, and underscores are allowed.',
        { field: 'name', pattern: validationRules.name.pattern.source },
        'name'
      );
    }

    // Description validation
    if (description && description.length > validationRules.description.maxLength) {
      throw this.createError(
        'validation_error',
        `Description must not exceed ${validationRules.description.maxLength} characters`,
        { field: 'description', maxLength: validationRules.description.maxLength },
        'description'
      );
    }
  }

  /**
   * Create a new workspace with comprehensive error handling
   * Uses authenticated client with explicit user filtering for security
   */
  async createWorkspace(params: CreateWorkspaceParams, authenticatedUserId: string, userEmail?: string): Promise<CreateWorkspaceResult> {
    if (!authenticatedUserId) {
      throw this.createError(
        'authentication_error',
        'User ID is required for workspace creation'
      );
    }

    try {
      // Parameter validation
      this.validateCreateParams(params);

      const trimmedName = params.name.trim();
      let workspaceDescription = params.description?.trim() || null;

      // Auto-generate description if enabled and not provided
      if (this.config.autoGenerateDescription && !workspaceDescription) {
        workspaceDescription = `Workspace for "${trimmedName}" keyword research and analysis`;
      }

      debugLog(userEmail, '[WorkspaceService] Creating workspace:', { 
        name: trimmedName, 
        description: workspaceDescription,
        userId: authenticatedUserId
      });


      // Create workspace in database with explicit user_id filtering
      const { data, error: dbError } = await supabase
        .from('workspaces')
        .insert({
          name: trimmedName,
          description: workspaceDescription,
          user_id: authenticatedUserId  // Explicit user filtering for security
          // Note: created_at, updated_at are handled by defaults
        })
        .select('*')
        .single();

      if (dbError) {
        debugError(userEmail, '[WorkspaceService] Database error:', dbError);
        
        // Handle specific database errors
        if (dbError.code === 'PGRST301') {
          throw this.createError(
            'authentication_error',
            'Authentication required to create workspace',
            dbError
          );
        }

        if (dbError.code === '23505') { // Unique constraint violation
          throw this.createError(
            'duplicate_name_error',
            'A workspace with this name already exists',
            dbError,
            'name'
          );
        }

        if (dbError.code === '42501') { // Insufficient privilege
          throw this.createError(
            'permission_error',
            'Permission denied to create workspace',
            dbError
          );
        }

        // Generic database error
        throw this.createError(
          'database_error',
          'Failed to create workspace. Please try again.',
          dbError
        );
      }

      if (!data) {
        throw this.createError(
          'database_error',
          'Workspace creation succeeded but no data returned',
          { params }
        );
      }

      debugLog(userEmail, '[WorkspaceService] Workspace created successfully:', data.id);

      // Initialize statistics for the new workspace to 0 
      // (This ensures the UI shows correct counts immediately)
      try {
        await this.refreshWorkspaceStatistics(authenticatedUserId, data.id);
      } catch (statsError) {
        debugWarn(userEmail, '[WorkspaceService] Failed to initialize workspace statistics:', statsError);
        // Don't fail workspace creation if statistics update fails
      }

      return {
        workspace: data as Workspace,
        success: true
      };

    } catch (error) {
      debugError(userEmail, '[WorkspaceService] Error creating workspace:', error);
      
      // Re-throw WorkspaceError objects as-is
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }

      // Handle network/connection errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw this.createError(
          'network_error',
          'Network connection failed. Please check your internet connection.',
          error
        );
      }

      // Generic error fallback
      throw this.createError(
        'unknown_error',
        error instanceof Error ? error.message : 'Failed to create workspace',
        error
      );
    }
  }

  /**
   * Refresh workspace statistics (keyword counts, search counts)
   * Fixed to calculate per-workspace keyword counts and global search counts
   * This should be called when data actually changes, not on every workspace fetch
   */
  async refreshWorkspaceStatistics(authenticatedUserId: string, workspaceId?: string, userEmail?: string): Promise<void> {
    if (!authenticatedUserId) {
      throw this.createError(
        'authentication_error',
        'User ID is required to refresh workspace statistics'
      );
    }

    try {

      // Get the workspaces to update
      const workspacesQuery = supabase
        .from('workspaces')
        .select('id')
        .eq('user_id', authenticatedUserId);
      
      if (workspaceId) {
        workspacesQuery.eq('id', workspaceId);
      }

      const { data: workspaces, error: workspacesError } = await workspacesQuery;

      if (workspacesError) {
        throw this.createError('database_error', 'Failed to fetch workspaces for statistics update', workspacesError);
      }

      if (!workspaces || workspaces.length === 0) {
        debugLog(userEmail, '[WorkspaceService] No workspaces found to update statistics');
        return;
      }

      // Calculate global search count for this user (search_history has no workspace_id)
      const { count: globalSearchesCount, error: searchesError } = await supabase
        .from('search_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', authenticatedUserId);

      if (searchesError) {
        debugWarn(userEmail, '[WorkspaceService] Could not fetch searches count:', searchesError);
      }

      // Calculate global AI analyses count (assuming ai_analyses table exists)
      const { count: globalAiAnalysesCount, error: aiAnalysesError } = await supabase
        .from('ai_analyses')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', authenticatedUserId);

      if (aiAnalysesError) {
        debugWarn(userEmail, '[WorkspaceService] Could not fetch AI analyses count:', aiAnalysesError);
      }

      // Update each workspace individually with its specific keyword count
      for (const workspace of workspaces) {
        // Calculate saved keywords count for this specific workspace
        const { count: workspaceKeywordsCount, error: keywordsError } = await supabase
          .from('keyword_consolidated_data')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', authenticatedUserId)
          .eq('workspace_id', workspace.id)
          .eq('saved', true);

         if (keywordsError) {
          debugWarn(userEmail, `[WorkspaceService] Could not fetch keywords count for workspace ${workspace.id}:`, keywordsError);
          continue; // Skip this workspace but continue with others
        }

        // Update this specific workspace with its own keyword count and global search count
        const { error: updateError } = await supabase
          .from('workspaces')
          .update({
            saved_keywords_count: workspaceKeywordsCount || 0,
            searches_performed_count: globalSearchesCount || 0, // Global count since search_history has no workspace_id
            ai_analyses_count: globalAiAnalysesCount || 0, // Global count
            updated_at: new Date().toISOString()
          })
          .eq('id', workspace.id)
          .eq('user_id', authenticatedUserId); // Extra security check

        if (updateError) {
          debugError(userEmail, `[WorkspaceService] Error updating statistics for workspace ${workspace.id}:`, updateError);
        } else {
          debugLog(userEmail, `[WorkspaceService] Updated statistics for workspace ${workspace.id}:`, {
            keywordCount: workspaceKeywordsCount || 0,
            searchCount: globalSearchesCount || 0,
            aiAnalysesCount: globalAiAnalysesCount || 0
          });
        }
      }

      debugLog(userEmail, '[WorkspaceService] Workspace statistics refresh completed for', workspaces.length, 'workspace(s)');

    } catch (error) {
      debugError(userEmail, '[WorkspaceService] Error in refreshWorkspaceStatistics:', error);
      
      // Re-throw WorkspaceError objects as-is
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }

      throw this.createError(
        'unknown_error',
        error instanceof Error ? error.message : 'Failed to refresh workspace statistics',
        error
      );
    }
  }

  /**
   * Get all workspaces for the current user
   * Uses authenticated client with explicit user filtering for security
   * OPTIMIZED: No longer refreshes statistics on every fetch - only when data changes
   */
  async getUserWorkspaces(authenticatedUserId: string, forceRefreshStats: boolean = false, userEmail?: string): Promise<Workspace[]> {
    if (!authenticatedUserId) {
      throw this.createError(
        'authentication_error',
        'User ID is required to fetch workspaces'
      );
    }

    try {
      debugLog(userEmail, '[WorkspaceService] Fetching user workspaces for:', authenticatedUserId);

      // Only refresh statistics if explicitly requested (e.g., after keyword operations)
      if (forceRefreshStats) {
        debugLog(userEmail, '[WorkspaceService] Force refreshing statistics before fetch');
        await this.refreshWorkspaceStatistics(authenticatedUserId, undefined, userEmail);
      }


      // Updated query to select all fields including the new statistics fields
      const { data, error: dbError } = await supabase
        .from('workspaces')
        .select(`
          id,
          user_id,
          name,
          description,
          created_at,
          updated_at,
          saved_keywords_count,
          ai_analyses_count,
          searches_performed_count,
          is_default
        `)
        .eq('user_id', authenticatedUserId)  // Explicit user filtering for security
        .order('updated_at', { ascending: false });

      if (dbError) {
        debugError(userEmail, '[WorkspaceService] Error fetching workspace:', dbError);
        
        // Handle specific database errors
        if (dbError.code === 'PGRST301') {
          throw this.createError(
            'authentication_error',
            'Authentication required to access workspaces',
            dbError
          );
        }

        // Check for invalid API key error
        if (dbError.message && dbError.message.includes('Invalid API key')) {
          throw this.createError(
            'authentication_error',
            'API authentication failed. Please refresh the page and try again.',
            dbError
          );
        }

        throw this.createError(
          'database_error',
          'Failed to fetch workspaces. Please try again.',
          dbError
        );
      }

      debugLog(userEmail, '[WorkspaceService] Retrieved workspaces:', data?.length || 0);
      return (data || []) as Workspace[];

    } catch (error) {
      debugError(userEmail, '[WorkspaceService] Error in getUserWorkspaces:', error);
      
      // Re-throw WorkspaceError objects as-is
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }

      // Handle network/connection errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw this.createError(
          'network_error',
          'Network connection failed. Please check your internet connection.',
          error
        );
      }

      // Generic error fallback
      throw this.createError(
        'unknown_error',
        error instanceof Error ? error.message : 'Failed to fetch workspaces',
        error
      );
    }
  }

  /**
   * Check if workspace name is available (optional utility)
   */
  async isWorkspaceNameAvailable(name: string, authenticatedUserId: string): Promise<boolean> {
    if (!authenticatedUserId) {
      return true; // Assume available if no user context
    }

    try {
      const trimmedName = name.trim();
      
      
      const { data, error } = await supabase
        .from('workspaces')
        .select('id')
        .eq('user_id', authenticatedUserId)  // Filter by user
        .eq('name', trimmedName)
        .limit(1);

      if (error) {
        debugError(undefined, '[WorkspaceService] Error checking name availability:', error);
        return true; // Assume available on error to allow creation attempt
      }

      return !data || data.length === 0;
    } catch (error) {
      debugError(undefined, '[WorkspaceService] Error in isWorkspaceNameAvailable:', error);
      return true; // Assume available on error
    }
  }

  /**
   * Generate user-friendly error messages
   */
  static getErrorMessage(error: WorkspaceError): string {
    const errorMessages: Record<WorkspaceErrorType, string> = {
      authentication_error: 'Please log in to create or access workspaces.',
      validation_error: error.message, // Use specific validation message
      database_error: 'Unable to save workspace. Please try again.',
      duplicate_name_error: 'A workspace with this name already exists. Please choose a different name.',
      permission_error: 'You don\'t have permission to perform this action.',
      network_error: 'Network connection failed. Please check your internet connection and try again.',
      unknown_error: 'An unexpected error occurred. Please try again.'
    };

    return errorMessages[error.type] || error.message;
  }
}

// Export singleton instance for use in hooks
export const workspaceService = new WorkspaceService();

// Export service functions for direct usage with explicit user ID requirements
export const createWorkspace = (params: CreateWorkspaceParams, authenticatedUserId: string, userEmail?: string) => 
  workspaceService.createWorkspace(params, authenticatedUserId, userEmail);

export const getUserWorkspaces = (authenticatedUserId: string, forceRefreshStats?: boolean, userEmail?: string) => 
  workspaceService.getUserWorkspaces(authenticatedUserId, forceRefreshStats, userEmail);

export const isWorkspaceNameAvailable = (name: string, authenticatedUserId: string) => 
  workspaceService.isWorkspaceNameAvailable(name, authenticatedUserId);

export const refreshWorkspaceStatistics = (authenticatedUserId: string, workspaceId?: string, userEmail?: string) => 
  workspaceService.refreshWorkspaceStatistics(authenticatedUserId, workspaceId, userEmail);
